import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';



const muscleGroups = ['all', 'chest', 'back', 'legs', 'shoulders', 'arms', 'core', 'full-body'];
const equipmentList = ['all', 'barbell', 'dumbbell', 'bodyweight', 'machine', 'cable', 'kettlebell', 'resistance band'];
const difficulties = ['all', 'beginner', 'intermediate', 'advanced'];

const difficultyColor = {
  beginner: { color: '#AAFF00', bg: 'rgba(170,255,0,0.1)' },
  intermediate: { color: '#FF6B00', bg: 'rgba(255,107,0,0.1)' },
  advanced: { color: '#ff4444', bg: 'rgba(255,68,68,0.1)' },
};

const categoryIcon = {
  strength: 'Dumbbell',
  cardio: 'Heart',
  flexibility: 'Sparkles',
  balance: 'Target',
  sports: 'Trophy',
};

const selectStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: '#fff',
  borderRadius: '8px',
  padding: '6px 8px',
  fontSize: '11px',
  outline: 'none',
  width: '100%',
};

const ExerciseCard = ({ exercise, onAdd, isAdded }) => (
  <motion.div
    layout
    className="p-3 rounded-xl transition-all"
    style={{
      background: isAdded ? 'rgba(170,255,0,0.05)' : 'rgba(255,255,255,0.03)',
      border: isAdded ? '1px solid rgba(170,255,0,0.25)' : '1px solid rgba(255,255,255,0.07)',
    }}
    whileHover={{ scale: 1.01 }}
  >
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.15)' }}>
        <Icon name={categoryIcon?.[exercise?.category] || 'Dumbbell'} size={16} style={{ color: '#00D4FF' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold truncate" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>{exercise?.name}</span>
          {exercise?.difficulty && (
            <span
              className="text-xs px-1.5 py-0.5 rounded-full font-medium"
              style={{
                color: difficultyColor?.[exercise?.difficulty]?.color || 'rgba(255,255,255,0.4)',
                background: difficultyColor?.[exercise?.difficulty]?.bg || 'rgba(255,255,255,0.06)',
              }}
            >
              {exercise?.difficulty}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {exercise?.muscleGroups?.slice(0, 2)?.map((m, i) => (
            <span key={i} className="text-xs px-1.5 py-0.5 rounded-full" style={{ color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.06)' }}>{m}</span>
          ))}
          {exercise?.equipmentNeeded?.slice(0, 1)?.map((e, i) => (
            <span key={i} className="text-xs px-1.5 py-0.5 rounded-full" style={{ color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.06)' }}>{e}</span>
          ))}
        </div>
      </div>
      <button
        onClick={() => onAdd(exercise)}
        disabled={isAdded}
        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all"
        style={{
          background: isAdded ? 'rgba(170,255,0,0.15)' : '#AAFF00',
          color: isAdded ? '#AAFF00' : '#0A0A0B',
          border: isAdded ? '1px solid rgba(170,255,0,0.3)' : 'none',
          boxShadow: isAdded ? 'none' : '0 0 8px rgba(170,255,0,0.3)',
          cursor: isAdded ? 'default' : 'pointer',
        }}
      >
        <Icon name={isAdded ? 'Check' : 'Plus'} size={14} />
      </button>
    </div>
  </motion.div>
);

const TrainerExerciseLibraryPanel = ({ exercises, customExercises, onAddExercise, addedExerciseIds, onAddCustomExercise }) => {
  const [search, setSearch] = useState('');
  const [muscle, setMuscle] = useState('all');
  const [equipment, setEquipment] = useState('all');
  const [difficulty, setDifficulty] = useState('all');
  const [tab, setTab] = useState('library');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customForm, setCustomForm] = useState({ name: '', description: '', notes: '' });

  const filtered = exercises?.filter(ex => {
    const matchSearch = ex?.name?.toLowerCase()?.includes(search?.toLowerCase());
    const matchMuscle = muscle === 'all' || ex?.muscleGroups?.includes(muscle);
    const matchEquip = equipment === 'all' || ex?.equipmentNeeded?.includes(equipment);
    const matchDiff = difficulty === 'all' || ex?.difficulty === difficulty;
    return matchSearch && matchMuscle && matchEquip && matchDiff;
  });

  const filteredCustom = customExercises?.filter(ex =>
    ex?.name?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const handleCreateCustom = () => {
    if (!customForm?.name?.trim()) return;
    onAddCustomExercise(customForm);
    setCustomForm({ name: '', description: '', notes: '' });
    setShowCustomForm(false);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(170,255,0,0.1)', border: '1px solid rgba(170,255,0,0.2)' }}>
          <Icon name="Library" size={16} style={{ color: '#AAFF00' }} />
        </div>
        <h2 className="text-base font-bold" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Exercise Library</h2>
        <span className="ml-auto text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{filtered?.length + filteredCustom?.length} exercises</span>
      </div>
      {/* Tab switcher */}
      <div className="flex gap-1 mb-3 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
        {['library', 'custom']?.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all"
            style={{
              background: tab === t ? 'rgba(255,255,255,0.08)' : 'transparent',
              color: tab === t ? '#fff' : 'rgba(255,255,255,0.4)',
              fontFamily: "'Inter Tight', 'Inter', sans-serif",
            }}
          >
            {t === 'library' ? 'Library' : `Custom (${customExercises?.length})`}
          </button>
        ))}
      </div>
      {/* Search */}
      <div data-tour="exercise-search" className="relative mb-3">
        <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.3)' }} />
        <input
          placeholder="Search exercises..."
          value={search}
          onChange={e => setSearch(e?.target?.value)}
          className="w-full pl-8 pr-3 py-2 rounded-lg text-sm focus:outline-none"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
        />
      </div>
      {tab === 'library' && (
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          <select value={muscle} onChange={e => setMuscle(e?.target?.value)} style={selectStyle}>
            {muscleGroups?.map(m => <option key={m} value={m} style={{ background: '#1a1a1b' }}>{m === 'all' ? 'All Muscles' : m}</option>)}
          </select>
          <select value={equipment} onChange={e => setEquipment(e?.target?.value)} style={selectStyle}>
            {equipmentList?.map(e => <option key={e} value={e} style={{ background: '#1a1a1b' }}>{e === 'all' ? 'All Equipment' : e}</option>)}
          </select>
          <select value={difficulty} onChange={e => setDifficulty(e?.target?.value)} style={selectStyle}>
            {difficulties?.map(d => <option key={d} value={d} style={{ background: '#1a1a1b' }}>{d === 'all' ? 'All Levels' : d}</option>)}
          </select>
        </div>
      )}
      {tab === 'custom' && (
        <div className="mb-3">
          <button
            onClick={() => setShowCustomForm(!showCustomForm)}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all"
            style={{ background: 'rgba(170,255,0,0.08)', border: '1px solid rgba(170,255,0,0.2)', color: '#AAFF00', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
          >
            <Icon name="Plus" size={14} />
            Add Custom Exercise
          </button>
          <AnimatePresence>
            {showCustomForm && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 p-3 rounded-xl space-y-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  {['name', 'description', 'notes']?.map((field) => (
                    <input
                      key={field}
                      placeholder={field === 'name' ? 'Exercise name *' : field === 'description' ? 'Description' : 'Trainer notes'}
                      value={customForm?.[field]}
                      onChange={e => setCustomForm(p => ({ ...p, [field]: e?.target?.value }))}
                      className="w-full px-3 py-2 rounded-lg text-xs focus:outline-none"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                    />
                  ))}
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateCustom}
                      className="flex-1 py-1.5 rounded-lg text-xs font-semibold"
                      style={{ background: '#AAFF00', color: '#0A0A0B', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                    >
                      Create
                    </button>
                    <button
                      onClick={() => setShowCustomForm(false)}
                      className="flex-1 py-1.5 rounded-lg text-xs font-semibold"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {tab === 'library' && filtered?.map(ex => (
          <ExerciseCard key={ex?.id} exercise={ex} onAdd={onAddExercise} isAdded={addedExerciseIds?.includes(ex?.id)} />
        ))}
        {tab === 'custom' && filteredCustom?.map(ex => (
          <ExerciseCard key={ex?.id} exercise={{ ...ex, category: 'strength', muscleGroups: [], equipmentNeeded: [] }} onAdd={onAddExercise} isAdded={addedExerciseIds?.includes(ex?.id)} />
        ))}
        {tab === 'library' && filtered?.length === 0 && (
          <div className="text-center py-8 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>No exercises match your filters</div>
        )}
        {tab === 'custom' && filteredCustom?.length === 0 && !showCustomForm && (
          <div className="text-center py-8 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>No custom exercises yet</div>
        )}
      </div>
    </div>
  );
};

export default TrainerExerciseLibraryPanel;
